import { css } from '@emotion/react';
import { PaymentPlanConfirmationStatus, PaymentPlanStatus } from '@frontend/api-payment-plans';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  usePopoverMenu,
  PopoverMenu,
  PopoverMenuItem,
  PayIcon,
  PauseIcon,
  EditSimpleIcon,
  CreditCardIcon,
  PrintIcon,
  ModalControlTriggerProps,
  PlayIcon,
  TrashIcon,
  BlockIcon,
  Button,
} from '@frontend/design-system';

export const PaymentPlanDetailsMenu = ({
  planStatus,
  confirmationStatus,
  handlePrint,
  editPlanTrigger,
  editBillingTriggerProps,
  pausePlanTriggerProps,
  cancelPlanTriggerProps,
  oneTimePaymentTriggerProps,
  deletePlanTriggerProps,
  hasPaymentMade,
  isMobile,
}: {
  planStatus: PaymentPlanStatus | undefined;
  confirmationStatus: PaymentPlanConfirmationStatus | undefined;
  handlePrint: () => void;
  editPlanTrigger: ModalControlTriggerProps;
  editBillingTriggerProps: ModalControlTriggerProps;
  pausePlanTriggerProps: ModalControlTriggerProps;
  cancelPlanTriggerProps: ModalControlTriggerProps;
  oneTimePaymentTriggerProps: ModalControlTriggerProps;
  deletePlanTriggerProps: ModalControlTriggerProps;
  hasPaymentMade: boolean;
  isMobile: boolean;
}) => {
  const { getTriggerProps, getMenuProps, close } = usePopoverMenu({ placement: 'bottom-end' });
  const triggerProps = getTriggerProps();
  const menuProps = getMenuProps();
  const { t } = useTranslation('payments');

  const onMenuClick = (menuItemOnClick: ModalControlTriggerProps['onClick']) => {
    menuItemOnClick();
    close();
  };

  const isPendingCustomerApproval =
    planStatus === PaymentPlanStatus.Paused && confirmationStatus === PaymentPlanConfirmationStatus.Pending;

  const isPausedPlan =
    planStatus === PaymentPlanStatus.Paused && confirmationStatus === PaymentPlanConfirmationStatus.PausedByOffice;

  const TRACKING_ID_BASE = 'pay-portal-paymentplans';

  return (
    <>
      <Button
        variant='secondary'
        size='large'
        hoverLabel={t('Action menu')}
        iconName='more'
        trackingId={`${TRACKING_ID_BASE}-btn-actionmenu`}
        {...triggerProps}
        css={
          !isMobile &&
          css`
            border-radius: ${theme.borderRadius.full};
            background-color: ${theme.colors.neutral10};
          `
        }
      />
      <PopoverMenu {...menuProps}>
        <PopoverMenuItem trackingId={`${TRACKING_ID_BASE}-menuitem-print`} Icon={PrintIcon} onClick={handlePrint}>
          {t('Print statement')}
        </PopoverMenuItem>
        {planStatus !== PaymentPlanStatus.Completed && planStatus !== PaymentPlanStatus.Canceled && (
          <>
            <PopoverMenuItem
              trackingId={`${TRACKING_ID_BASE}-menuitem-editbilling`}
              Icon={CreditCardIcon}
              onClick={() => onMenuClick(editBillingTriggerProps.onClick)}
            >
              {t('Edit billing method')}
            </PopoverMenuItem>
            <PopoverMenuItem
              trackingId={`${TRACKING_ID_BASE}-menuitem-edit`}
              Icon={isPendingCustomerApproval || !isPausedPlan ? EditSimpleIcon : PlayIcon}
              onClick={() => onMenuClick(editPlanTrigger.onClick)}
            >
              {isPendingCustomerApproval || !isPausedPlan ? t('Edit payment plan') : t('Resume payment plan')}
            </PopoverMenuItem>

            {!isPausedPlan && (
              <PopoverMenuItem
                trackingId={`${TRACKING_ID_BASE}-menuitem-pause`}
                Icon={PauseIcon}
                onClick={() => onMenuClick(pausePlanTriggerProps.onClick)}
              >
                {t('Pause payment plan')}
              </PopoverMenuItem>
            )}
            <PopoverMenuItem
              trackingId={`${TRACKING_ID_BASE}-menuitem-onetimepayment`}
              Icon={PayIcon}
              onClick={() => onMenuClick(oneTimePaymentTriggerProps.onClick)}
            >
              {t('Make a one-time payment')}
            </PopoverMenuItem>
            {hasPaymentMade ? (
              <PopoverMenuItem
                trackingId={`${TRACKING_ID_BASE}-menuitem-cancel`}
                Icon={BlockIcon}
                onClick={() => onMenuClick(cancelPlanTriggerProps.onClick)}
              >
                {t('Cancel payment plan')}
              </PopoverMenuItem>
            ) : (
              <PopoverMenuItem
                trackingId={`${TRACKING_ID_BASE}-menuitem-delete`}
                Icon={TrashIcon}
                onClick={() => onMenuClick(deletePlanTriggerProps.onClick)}
              >
                {t(`Delete payment plan`)}
              </PopoverMenuItem>
            )}
          </>
        )}
      </PopoverMenu>
    </>
  );
};
